import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IconType, MapApiService } from '../services';

@Component({
    selector: 'rwidget-map-icon',
    template: '<ng-content></ng-content>',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapIconComponent implements OnInit, OnDestroy {
    private content: HTMLElement;

    private _url: string;
    private cancelSvgLoad$ = new Subject<void>();

    @Input()
    set url(value: string) {
        if (!value || value === this._url) return;

        this._url = value;
        this.reloadIcon();
    }

    @Input() retinaUrl: string;

    @Input() size: number[];

    @Input() anchor: number[];

    @Input() popupAnchor = [0, -34];

    @Input() forceContentClone = false;

    @Input() iconClass: string;

    @Input() preserveFill = false;

    constructor(
        private readonly mapApi: MapApiService,
        private readonly eRef: ElementRef,
        private readonly http: HttpClient,
        private readonly renderer: Renderer2
    ) {}

    ngOnInit(): void {
        const hasContent = this.eRef.nativeElement.childNodes[0];

        if (!hasContent && this._url && this._url.endsWith('.svg')) {
            // Load SVG inline so it can be styled with CSS
            this.loadInlineSvg(this._url);
        } else {
            if (!this.size && this._url) {
                const img = new Image();
                img.src = this._url;
                img.onload = (e) => {
                    const currentTarget = e.currentTarget as HTMLImageElement;
                    this.size = [currentTarget.width, currentTarget.height];
                };
            }
            this.content = hasContent;
        }
    }

    private reloadIcon(): void {
        this.clearContent();
        if (this._url.endsWith('.svg')) {
            this.loadInlineSvg(this._url);
        } else {
            this.loadImageIcon(this._url);
        }
    }

    private clearContent(): void {
        this.cancelSvgLoad$.next();
        if (this.content && this.content.parentNode === this.eRef.nativeElement) {
            this.renderer.removeChild(this.eRef.nativeElement, this.content);
        }

        this.content = null;
    }

    private loadInlineSvg(url: string): void {
        this.http
            .get(url, { responseType: 'text' })
            .pipe(takeUntil(this.cancelSvgLoad$))
            .subscribe((svgContent) => {
                const wrapper = this.renderer.createElement('div');
                wrapper.innerHTML = svgContent;
                const svgElement = wrapper.querySelector('svg');

                if (!svgElement) return;

                // Apply CSS class if provided
                this.iconClass && this.applyIconClass(svgElement);

                this.renderSvg(svgElement);
                this.content = svgElement;
            });
    }

    private renderSvg(svgElement: SVGElement): void {
        if (!this.preserveFill) {
            this.renderer.setAttribute(svgElement, 'fill', svgElement.getAttribute('fill') || 'currentColor');
        }
        this.renderer.appendChild(this.eRef.nativeElement, svgElement);
    }

    private applyIconClass(svgElement: SVGElement): void {
        this.iconClass.split(' ').forEach((cls) => this.renderer.addClass(svgElement, cls));
    }

    private loadImageIcon(url: string): void {
        if (this.size) return;

        const img = new Image();
        img.src = url;
        img.onload = (e) => {
            const image = e.currentTarget as HTMLImageElement;
            !this.size && (this.size = [image.width, image.height]);
        };
    }

    private getDivIcon(): IconType {
        const options = {
            html: this.forceContentClone ? this.content.cloneNode(true) : this.content,
            iconSize: this.size,
            iconAnchor: this.anchor,
            popupAnchor: this.popupAnchor
        };

        return this.mapApi.createIcon(options, true);
    }

    private getImageIcon(): IconType {
        const options = {
            iconUrl: this._url,
            iconSize: this.size, // size of the icon
            popupAnchor: this.popupAnchor // point from which the popup should open relative to the iconAncho
        };

        if (this.retinaUrl) {
            options['iconRetinaUrl'] = this.retinaUrl;
        }

        if (this.anchor) {
            options['iconAnchor'] = this.anchor; // point of the icon which will correspond to marker's location
        }

        return this.mapApi.createIcon(options);
    }

    getIcon(): IconType {
        return this.content ? this.getDivIcon() : this.getImageIcon();
    }

    ngOnDestroy(): void {
        this.cancelSvgLoad$.next();
        this.cancelSvgLoad$.complete();
        this.content = null;
    }
}
